import { useState } from 'react';
import { C, DEFAULT_TEMPLATES } from '../config';

function AdminPage({ templates, setTemplates, onBack, user }) {
  const [editing, setEditing] = useState(null);
  const [editBuf, setEditBuf] = useState({ label:"", body:"" });
  const [saved,   setSaved]   = useState(false);

  if (!user?.isAdmin) return (
    <div style={{ maxWidth:440, margin:"80px auto", textAlign:"center" }}>
      <div style={{ fontSize:40, marginBottom:12 }}>🔒</div>
      <div style={{ fontSize:18, fontWeight:800, color:C.text, marginBottom:8 }}>Admin Access Required</div>
      <div style={{ fontSize:13, color:C.muted, lineHeight:1.7, marginBottom:24 }}>
        This page is restricted to members of<br />
        <strong style={{ color:C.text, fontFamily:"'IBM Plex Mono',monospace" }}>{ADMIN_GROUP}</strong><br />
        in Google Workspace. Sign in with your Sleep Metrics Google account to continue.
      </div>
      <button onClick={onBack} style={{
        background:"none", border:`1px solid ${C.border}`, color:C.muted,
        borderRadius:8, padding:"10px 20px", fontSize:13, cursor:"pointer", fontFamily:"inherit",
      }}>← Back to Dashboard</button>
    </div>
  );

  const startEdit = (t) => { setEditing(t.id); setEditBuf({ label:t.label, body:t.body }); setSaved(false); };
  const saveEdit  = () => {
    setTemplates(prev => prev.map(t => t.id===editing ? { ...t, ...editBuf } : t));
    setSaved(true); setTimeout(() => { setEditing(null); setSaved(false); }, 900);
  };
  const addTemplate    = () => {
    const id = "custom_" + Date.now();
    setTemplates(prev => [...prev, { id, label:"New Template", body:"Hi {first}, " }]);
    setEditing(id); setEditBuf({ label:"New Template", body:"Hi {first}, " });
  };
  const deleteTemplate = (id) => { setTemplates(prev => prev.filter(t => t.id !== id)); if (editing===id) setEditing(null); };

  const VARS = ["{first}","{last}","{days}","{days4hr}","{daysUsed}","{daysLeft}","{pct}","{device}","{insurer}"];

  return (
    <div style={{ maxWidth:900, margin:"0 auto" }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20 }}>
        <div>
          <div style={{ fontSize:17, fontWeight:800, color:C.text }}>⚙️ Template Manager</div>
          <div style={{ fontSize:12, color:C.muted, marginTop:3 }}>
            Signed in as <strong style={{ color:C.text }}>{user.name}</strong> · {user.email}
            <span style={{ marginLeft:8, background:C.green+"18", color:C.green, border:`1px solid ${C.green}30`, borderRadius:4, padding:"1px 7px", fontSize:10, fontWeight:700 }}>ADMIN</span>
          </div>
        </div>
        <div style={{ display:"flex", gap:10 }}>
          <button onClick={addTemplate} style={{ background:C.teal, border:"none", color:"#fff", borderRadius:8, padding:"9px 18px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>+ New Template</button>
          <button onClick={onBack} style={{ background:"none", border:`1px solid ${C.border}`, color:C.muted, borderRadius:8, padding:"9px 16px", fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>← Back</button>
        </div>
      </div>

      {/* Variable reference */}
      <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10, padding:"12px 16px", marginBottom:18 }}>
        <div style={{ fontSize:11, fontWeight:700, color:C.muted, marginBottom:8, textTransform:"uppercase", letterSpacing:"0.08em" }}>Available Variables — click to copy</div>
        <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
          {VARS.map(v => (
            <button key={v} onClick={() => navigator.clipboard.writeText(v)} style={{
              background:C.blue+"10", border:`1px solid ${C.blue}28`, color:C.blue,
              borderRadius:5, padding:"3px 9px", fontSize:11, fontWeight:700,
              cursor:"pointer", fontFamily:"'IBM Plex Mono',monospace",
            }}>{v}</button>
          ))}
        </div>
      </div>

      <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
        {templates.map(t => (
          <div key={t.id} style={{
            background: editing===t.id ? C.surface : C.card,
            border:`1px solid ${editing===t.id ? C.teal+"55" : C.border}`,
            borderLeft:`3px solid ${editing===t.id ? C.teal : C.faint}`,
            borderRadius:10, overflow:"hidden",
          }}>
            {editing===t.id ? (
              <div style={{ padding:"16px 18px" }}>
                <div style={{ marginBottom:11 }}>
                  <div style={{ fontSize:11, color:C.muted, fontWeight:600, marginBottom:5 }}>Template Name</div>
                  <input value={editBuf.label} onChange={e=>setEditBuf(p=>({...p,label:e.target.value}))} style={{ width:"100%", background:C.card, border:`1px solid ${C.border}`, borderRadius:7, padding:"8px 12px", fontSize:13, fontFamily:"inherit", outline:"none", color:C.text }} />
                </div>
                <div style={{ marginBottom:11 }}>
                  <div style={{ fontSize:11, color:C.muted, fontWeight:600, marginBottom:5 }}>Message Body</div>
                  <textarea value={editBuf.body} onChange={e=>setEditBuf(p=>({...p,body:e.target.value}))} rows={4} style={{ width:"100%", background:C.card, border:`1px solid ${C.border}`, borderRadius:7, padding:"10px 12px", fontSize:13, fontFamily:"inherit", outline:"none", resize:"vertical", color:C.text, lineHeight:1.6 }} />
                  <div style={{ fontSize:10, color:C.muted, marginTop:4 }}>Variables are replaced with real patient data when sent.</div>
                </div>
                <div style={{ background:"#EEF0F7", border:`1px solid ${C.border}`, borderRadius:7, padding:"10px 12px", marginBottom:12 }}>
                  <div style={{ fontSize:10, color:C.muted, fontWeight:700, marginBottom:4, textTransform:"uppercase" }}>Preview (sample patient)</div>
                  <div style={{ fontSize:12, color:C.text, lineHeight:1.6 }}>
                    {renderTemplate(editBuf.body, { first:"Alex", last:"Smith", days:14, days4hr:8, daysUsed:14, pct:57, device:"AirSense 11", insurer:"BCBS" })}
                  </div>
                </div>
                <div style={{ display:"flex", gap:8 }}>
                  <button onClick={saveEdit} style={{ background:saved?C.green:C.teal, border:"none", color:"#fff", borderRadius:7, padding:"9px 20px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>{saved ? "✓ Saved!" : "Save Changes"}</button>
                  <button onClick={()=>setEditing(null)} style={{ background:"none", border:`1px solid ${C.border}`, color:C.muted, borderRadius:7, padding:"9px 16px", fontSize:12, cursor:"pointer", fontFamily:"inherit" }}>Cancel</button>
                </div>
              </div>
            ) : (
              <div style={{ display:"flex", alignItems:"center", padding:"13px 18px", gap:14 }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, fontWeight:700, color:C.text }}>{t.label}</div>
                  <div style={{ fontSize:12, color:C.muted, marginTop:3, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{t.body.substring(0,110)}{t.body.length>110?"…":""}</div>
                </div>
                <div style={{ display:"flex", gap:8, flexShrink:0 }}>
                  <button onClick={()=>startEdit(t)} style={{ background:C.blue+"10", border:`1px solid ${C.blue}28`, color:C.blue, borderRadius:6, padding:"6px 14px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>Edit</button>
                  <button onClick={()=>deleteTemplate(t.id)} style={{ background:C.red+"10", border:`1px solid ${C.red}28`, color:C.red, borderRadius:6, padding:"6px 14px", fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>Delete</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default AdminPage;
